import { useState } from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faShieldAlt,
  faUserTag,
  faKey,
  faUser,
  faIdCard,
} from "@fortawesome/free-solid-svg-icons";
import PermissionTab from "./PermissionTab";
import RoleTab from "./RoleTab";
import ChangePasswordTab from "./ChangePasswordTab";
import { statusStyles } from "../../../utils/data";
import { userService } from "../../../services/service";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const UserDetailView = ({
  user,
  onBack,
  permissions,
  setPermissions,
  onUpdateUser,
}) => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState(null);

  const canViewPermissions =
    hasPermission(["manage_assign_permission", "read_assign_permission"]) &&
    hasPermission(["manage_permission", "read_permission"]);
  const canViewRole =
    hasPermission(["read_user_account"]) ||
    (hasPermission(["manage_user_account"]) &&
      hasPermission(["read_user_account"]) &&
      hasPermission(["read_user_login"]));
  const canViewLogin = hasPermission(["manage_user_login"]);

  const tabs = [
    {
      key: "permissions",
      label: "Permissions",
      icon: faShieldAlt,
      show: canViewPermissions,
    },
    {
      key: "role",
      label: "Role",
      icon: faUserTag,
      show: canViewRole,
    },
    {
      key: "change-password",
      label: "Change Password",
      icon: faKey,
      show: canViewLogin,
    },
  ].filter((t) => t.show);

  const currentTab = tabs.some((t) => t.key === activeTab)
    ? activeTab
    : tabs[0]?.key;

  const handleUpdateUser = async (updatedUser) => {
    const canChangeRole =
      hasPermission(["manage_user_account"]) &&
      hasPermission(["read_user_account"]) &&
      hasPermission(["read_user_login"]);
    if (!canChangeRole) return;

    try {
      const response = await userService.update(
        updatedUser.userId,
        updatedUser,
      );
      if (response?.data) {
        toastSuccess("User updated successfully!");
        if (onUpdateUser) {
          onUpdateUser(updatedUser);
        }
      }
    } catch (error) {
      console.error("Failed to update user:", error);
      toastError(
        error.response?.data?.message ||
          "Failed to update user. Please try again.",
      );
    }
  };

  const handleChangePassword = async (passwordData) => {
    if (!hasPermission(["manage_user_login"])) return;
    try {
      const response = await userService.changePassword(passwordData);
      if (response?.status === "success") {
        toastSuccess("Password updated successfully!");
      }
    } catch (error) {
      console.error("Failed to change password:", error);
      toastError(
        error.response?.data?.message ||
          "Failed to change password. Please try again.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Back Button */}
      <Card className="bg-white border border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-800 text-white hover:bg-blue-900 transition-all"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
            Back to Users
          </button>
          <Badge variant="outline" className={statusStyles[user.status]}>
            {user.status}
          </Badge>
        </div>
      </Card>

      {/* User Info Cards (always visible) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Card className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Full Name
            </p>
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 text-blue-500">
              <FontAwesomeIcon icon={faUser} className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {user.full_name}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Reg Number
            </p>
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-purple-100 text-purple-500">
              <FontAwesomeIcon icon={faIdCard} className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {user.customer_reg_no || user.username}
          </p>
        </Card>

        <Card className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
              Role
            </p>
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-100 text-amber-500">
              <FontAwesomeIcon icon={faUserTag} className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900">{user.role}</p>
        </Card>
      </div>

      {/* Tabs (only the ones the user can see) */}
      {tabs.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1 border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  currentTab === tab.key
                    ? "border-blue-800 text-blue-800"
                    : "border-transparent text-gray-600 hover:text-gray-700"
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {currentTab === "permissions" && (
            <PermissionTab
              user={user}
              permissions={permissions}
              setPermissions={setPermissions}
            />
          )}

          {currentTab === "role" && (
            <RoleTab user={user} onSave={handleUpdateUser} />
          )}

          {currentTab === "change-password" && (
            <ChangePasswordTab
              user={user}
              onSave={handleChangePassword}
              onUserUpdated={onUpdateUser}
            />
          )}
        </>
      )}
    </div>
  );
};

export default UserDetailView;
